/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
import { SagaIterator } from 'redux-saga';
/**
 *
 * @author Mikhail Kokoulin
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.11.0
 * @since 1.0.0
 */
import { call, put, take, takeEvery } from 'redux-saga/effects';

import { loadAuthDataSaga } from '../../pages/PageAuth/sagas/authSaga';
import { requestSaga } from '../../sagas/requestSaga';
import {
    LOAD_EXTERNAL_TRANSLATIONS_FINISH,
    LOAD_INTERNAL_TRANSLATIONS_SUCCESS,
    TranslationFileNamesEnum,
} from '../internationalization/constants';
import { loadExternalTranslationsStart, loadInternalTranslationsStart } from '../internationalization/intActions';
import { hideLoader, showLoader } from '../loader/loaderActions';
import { loadStorageDataSaga } from '../storage/storageSagas';
import { API_HOSTNAME, APP_CONFIG } from './Api';
import { initAppFail, initAppSuccess, loadAppConfigFailure, loadAppConfigSuccess, setInitAppError } from './appActions';
import { INIT_APP_START } from './appConstants';
import { IAppConfig } from './appTypes';

/**
 * Saga initializing the main component
 * @returns {IterableIterator<any>}
 */
export function* initializeApp(): SagaIterator {
    try {
        yield put(showLoader());
        const response: IAppConfig = yield call(requestSaga, API_HOSTNAME + APP_CONFIG, 'GET');
        yield put(loadAppConfigSuccess(response));

        yield put(loadExternalTranslationsStart()); // should be before Internal Translations
        yield take(LOAD_EXTERNAL_TRANSLATIONS_FINISH);
        yield put(loadInternalTranslationsStart([
            TranslationFileNamesEnum.common,
            TranslationFileNamesEnum.corporate,
        ]));
        yield take(LOAD_INTERNAL_TRANSLATIONS_SUCCESS);
        yield call(loadStorageDataSaga);

        // Check auth
        yield call(loadAuthDataSaga);

        yield put(initAppSuccess());
    } catch (error: any) {
        yield put(loadExternalTranslationsStart()); // should be before Internal Translations
        yield take(LOAD_EXTERNAL_TRANSLATIONS_FINISH);
        yield put(loadInternalTranslationsStart([
            TranslationFileNamesEnum.common,
        ]));
        yield take(LOAD_INTERNAL_TRANSLATIONS_SUCCESS);
        if (error.code >= 500 && error.code <= 599) {
            yield put(setInitAppError({
                hasInitError: true,
                code: error.code,
            }));
        }
        yield put(initAppFail(error));
        yield put(loadAppConfigFailure(error));
    } finally {
        document.body.classList.remove('app-loading');
        document.body.classList.add('app-ready');
        yield put(hideLoader());
    }
}

export function* appSagas(): SagaIterator {
    yield takeEvery(INIT_APP_START, initializeApp);
}
